import React, { useMemo, useCallback, useState, useEffect } from 'react';
import { Box, Typography, Tooltip } from '@mui/material';
import { Table, Tag, Spin, Pagination } from 'antd';
import {
  Business as ClientIcon,
  LocationOn as LocationIcon,
  LocalShipping as VendorIcon,
} from '@mui/icons-material';
import { useQuery } from '@tanstack/react-query';
import { Location, LocationResponse, FilterState, Vendor, Prospect } from '../../../types';
import { fetchLocations, fetchWarehouses, fetchVendors, fetchProspects } from '../../../services/api';

import type { Map } from 'ol';
import { fromLonLat } from 'ol/proj';
import type { Vector as VectorSource } from 'ol/source';
import type { Point } from 'ol/geom';
import type { Overlay } from 'ol';
import { PopupTableRow, formatCurrency, maskEmail, maskPhone } from '../utils';

// Utility function to parse amount that may include K/M suffix
const parseAmount = (amount: string | number): number => {
  if (typeof amount === 'string') {
    if (amount.endsWith('K')) {
      return parseFloat(amount.slice(0, -1)) * 1000;
    }
    if (amount.endsWith('M')) {
      return parseFloat(amount.slice(0, -1)) * 1000000;
    }
    return parseFloat(amount) || 0;
  }
  return amount || 0;
};

const CLIENT_PAGE_SIZE = 10;
const VENDOR_PAGE_SIZE = 10;
const PROSPECT_PAGE_SIZE = 10;

interface SearchResultsProps {
  filters: FilterState;
  map: Map;
  clientSource: VectorSource;
  vendorSource: VectorSource;
  prospectSource: VectorSource;
  popup: Overlay;
  popupRef: React.RefObject<HTMLDivElement>;
}

// Memoized item components

const SearchResults: React.FC<SearchResultsProps> = ({ 
  filters, 
  map, 
  clientSource, 
  vendorSource,
  prospectSource,
  popup, 
  popupRef 
}) => {
  const [clientPage, setClientPage] = useState(1);
  const [vendorPage, setVendorPage] = useState(1);
  const [prospectPage, setProspectPage] = useState(1);
  const locationTypeKey = Array.isArray(filters.locationType) ? filters.locationType.join(',') : '';

  useEffect(() => {
    setClientPage(1);
  }, [filters.country, filters.state, filters.city, filters.productSearch, filters.salesRep, filters.searchTerm, filters.startDate, filters.endDate, filters.dateRangePreset]);

  useEffect(() => {
    setVendorPage(1);
  }, [filters.country, filters.state, filters.city, filters.searchTerm, filters.startDate, filters.endDate, filters.dateRangePreset, locationTypeKey]);

  useEffect(() => {
    setProspectPage(1);
  }, [filters.country, filters.state, filters.city, filters.salesRep, filters.searchTerm, filters.productSearch, filters.startDate, filters.endDate, filters.dateRangePreset]);

  const { data: response = { locations: [], total_amount: 0 } } = useQuery<LocationResponse>({
    queryKey: ['locations', filters.productSearch, filters.country, filters.state, filters.salesRep, filters.city, filters.startDate, filters.endDate, filters.dateRangePreset],
    queryFn: () => fetchLocations(filters.productSearch, filters.country, filters.state, filters.salesRep, filters.city, undefined, filters.startDate, filters.endDate),
    staleTime: 300000 // Cache for 5 minutes
  });

  const { data: warehouses = [] } = useQuery({
    queryKey: ['warehouses', filters.country, filters.state, filters.startDate, filters.endDate, filters.dateRangePreset],
    queryFn: () => fetchWarehouses(filters.country, filters.state),
    staleTime: 300000
  });

  const { data: vendors = [], isLoading: isLoadingVendors } = useQuery({
    queryKey: ['vendors', filters.productSearch, filters.country, filters.state, filters.city, filters.startDate, filters.endDate, filters.dateRangePreset],
    queryFn: () => fetchVendors(filters.productSearch, filters.country, filters.state, filters.city, filters.startDate, filters.endDate),
    staleTime: 300000,
    enabled: filters.locationType.includes('vendor') // Only fetch when vendor type is selected
  });

  const { data: prospects = [] } = useQuery({
    queryKey: ['prospects', filters.country, filters.state, filters.salesRep, filters.city, filters.startDate, filters.endDate, filters.productSearch, filters.dateRangePreset],
    queryFn: () => fetchProspects(filters.country, filters.state, filters.salesRep, filters.city, filters.productSearch, filters.startDate, filters.endDate),
    staleTime: 300000,
    enabled: !!(filters.searchTerm || filters.productSearch) // Only fetch when there's a search term
  });

  // Memoize filtered results
  const {
    filteredLocations,
    filteredWarehouses,
    filteredVendors,
    filteredProspects
  } = useMemo(() => {
    const filteredVendors = vendors.filter(vendor => 
      vendor.coordinates && (!filters.searchTerm || vendor.company.toLowerCase().includes(filters.searchTerm.toLowerCase()))
    );

    return {
      filteredLocations: response.locations.filter((location: Location) => 
        !filters.searchTerm || location.company.toLowerCase().includes(filters.searchTerm.toLowerCase())
      ),
      filteredWarehouses: warehouses.filter(warehouse => 
        !filters.searchTerm || warehouse.name.toLowerCase().includes(filters.searchTerm.toLowerCase())
      ),
      filteredVendors,
      filteredProspects: Array.isArray(prospects) && !filters.productSearch ?
      prospects.filter(prospect => {
        // Only filter by company name and only when not doing product search
        return !filters.searchTerm || prospect.company.toLowerCase().includes(filters.searchTerm.toLowerCase());
      }) : []
    };
  }, [response.locations, warehouses, vendors, prospects, filters.searchTerm, filters.productSearch]);

  const totalClientPages = Math.max(1, Math.ceil(filteredLocations.length / CLIENT_PAGE_SIZE));
  const totalVendorPages = Math.max(1, Math.ceil(filteredVendors.length / VENDOR_PAGE_SIZE));
  const totalProspectPages = Math.max(1, Math.ceil(filteredProspects.length / PROSPECT_PAGE_SIZE));

  const clientPageSafe = Math.min(clientPage, totalClientPages);
  const vendorPageSafe = Math.min(vendorPage, totalVendorPages);
  const prospectPageSafe = Math.min(prospectPage, totalProspectPages);

  useEffect(() => {
    if (clientPage !== clientPageSafe) {
      setClientPage(clientPageSafe);
    }
  }, [clientPage, clientPageSafe]);

  useEffect(() => {
    if (vendorPage !== vendorPageSafe) {
      setVendorPage(vendorPageSafe);
    }
  }, [vendorPage, vendorPageSafe]);

  useEffect(() => {
    if (prospectPage !== prospectPageSafe) {
      setProspectPage(prospectPageSafe);
    }
  }, [prospectPage, prospectPageSafe]);

  const paginatedProspects = useMemo(() => {
    const start = (prospectPageSafe - 1) * PROSPECT_PAGE_SIZE;
    return filteredProspects.slice(start, start + PROSPECT_PAGE_SIZE);
  }, [filteredProspects, prospectPageSafe]);

  // Memoize click handlers
  const handleLocationClick = useCallback(async (properties: any, type: string) => {
    try {
      const row: PopupTableRow = {
        type: type as PopupTableRow['type'],
        name: properties.company || properties.name || '',
        userid: properties.userid || properties.properties?.userid,
        location_id: properties.id || '',
        address: properties.address || '',
        city: properties.city || '',
        state: properties.state || '',
        zip: properties.zip || '',
        country: properties.country?.short_name || '',
        staff: properties.staff_name || '',
        orders: properties.order_count ?? undefined,
        products: properties.unique_products ?? undefined,
        quotation_count: properties.quotation_count ?? undefined,
        enquiry_count: properties.enquiry_count ?? undefined,
        open_so: typeof properties.open_so === 'string' ? properties.open_so : '',
        target: properties.target ?? undefined,
        target_year: properties.target_year ?? undefined,
        target_achievement: properties.target_achievement ?? undefined,
        total_amount: properties.total_amount ?? undefined,
        email: properties.email_1 || '',
        phone: properties.phonenumber || '',
        website_validation_notes: properties.website_validation_notes || '',
        website_found_address: properties.website_found_address || '',
        website_found_city: properties.website_found_city || '',
        website_found_state: properties.website_found_state || '',
        website_found_country: properties.website_found_country || '',
      };
      const event = new CustomEvent('open-table-drawer', { detail: { rows: [row] } });
      window.dispatchEvent(event);
    } catch (error) {
      console.error('Error preparing drawer content:', error);
    }
  }, []);

  const findFeatureAndClick = useCallback((item: any, source: VectorSource, type: string) => {
    if (!item.coordinates) return;
    
    const coordinates = item.coordinates;
    const itemCoords = fromLonLat(coordinates);

    // Zoom to location when clicking from search results
    map.getView().animate({
      center: itemCoords,
      zoom: 12,
      duration: 1000
    });

    // First try to find an exact match
    let feature = source.getFeatures().find(f => {
      const geometry = f.getGeometry() as Point;
      const featureCoords = geometry.getCoordinates();
      return featureCoords[0] === itemCoords[0] && featureCoords[1] === itemCoords[1];
    });

    // If no exact match found, look for a cluster that contains this item
    if (!feature) {
      feature = source.getFeatures().find(f => {
        const clusterFeatures = f.get('features');
        if (!clusterFeatures) return false;
        
        return clusterFeatures.some((cf: any) => {
          const props = cf.get('properties');
          return props.company === item.company && 
                 props.coordinates[0] === item.coordinates[0] && 
                 props.coordinates[1] === item.coordinates[1];
        });
      });
    }
    
    if (feature) {
      // Always show single item popup when clicking from search results
      handleLocationClick(item, type);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [handleLocationClick, map, popup, popupRef]);

const ProspectItem = React.memo(({ prospect, onClick }: { prospect: Prospect; onClick: () => void }) => (
  <Box 
    className="company-item prospect"
    sx={{ 
      cursor: 'pointer',
      p: 2,
      mb: 1,
      borderRadius: 1,
      backgroundColor: 'white',
      borderLeft: '4px solid #1a73e8',
      boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
      '&:hover': {
        boxShadow: '0 2px 5px rgba(0,0,0,0.15)',
        transform: 'translateY(-1px)',
        transition: 'all 0.2s ease'
      }
    }}
    onClick={onClick}
  >
    <Typography variant="subtitle1" sx={{ color: '#1a73e8', fontWeight: 600 }}>
      {prospect.company}
    </Typography>
    <Typography variant="body2" color="text.secondary">
      {[prospect.city, prospect.state].filter(Boolean).join(', ')}
    </Typography>
    {prospect.country?.short_name && (
      <Typography variant="body2" color="text.secondary">
        {prospect.country.short_name}
      </Typography>
    )}
    {prospect.staff_name && (
      <Box sx={{ mt: 1 }}>
        <Typography variant="body2" color="text.secondary">
          Sales Rep: {prospect.staff_name}
        </Typography>
      </Box>
    )}
    {prospect.products && prospect.products.length > 0 && (
      <Box sx={{ mt: 1 }}>
        <Typography variant="body2" color="text.secondary">
          Products: {prospect.products.join(', ')}
        </Typography>
      </Box>
    )}
  </Box>
));

return (
    <Box className="search-results">
      {filteredLocations.length > 0 && (
        <>
          <Box className="results-section-header" sx={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center', 
            p: 2,
            mb: 1,
            backgroundColor: '#f8f9fa',
            borderRadius: 1,
            boxShadow: '0 1px 2px rgba(0,0,0,0.05)'
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <ClientIcon sx={{ color: 'rgb(0, 145, 5)' }} />
              <Typography>Clients ({filteredLocations.length})</Typography>
            </Box>
            <Typography sx={{ color: 'rgb(0, 145, 5)', fontWeight: 600 }}>
              {formatCurrency(filteredLocations.reduce((sum, location) => sum + parseAmount(location.total_amount), 0))}
            </Typography>
          </Box>
          <Table
            columns={[
              {
                title: 'Location',
                dataIndex: 'location',
                key: 'location',
                width: '250px',
              },
              {
                title: 'City',
                dataIndex: 'city',
                key: 'city',
                width: '250px',
              },
              {
                title: 'Staff',
                dataIndex: 'staff',
                key: 'staff',
              },
              {
                title: 'Order',
                dataIndex: 'order',
                key: 'order',
              },
              {
                title: 'Products',
                dataIndex: 'products',
                key: 'products',
              },
              {
                title: 'Total',
                dataIndex: 'total',
                key: 'total',
                render: (text) => <Tag color='geekblue'>{text}</Tag>,
              },
            ]}
            dataSource={filteredLocations.map((location: Location, index: number) => ({
              key: `client-${index}`,
              location: location.company,
              city: `${location.city}, ${location.state} • ${location.country?.short_name}`,
              staff: location.staff_name || 'No staff assigned',
              order: location.order_count,
              products: location.unique_products,
              total: formatCurrency(parseAmount(location.total_amount)),
              onClick: () => findFeatureAndClick(location, clientSource, 'client')
            }))}
            onRow={(record) => ({
              onClick: record.onClick,
            })}
            pagination={{
              current: clientPageSafe,
              pageSize: CLIENT_PAGE_SIZE,
              total: filteredLocations.length,
              onChange: (page) => setClientPage(page),
              showSizeChanger: false,
              showQuickJumper: false,
              size: 'small'
            }}
            size="small"
            className='border'
            scroll={{x : 'max-content'}}
          />
        </>
      )}

      {/* Show vendors when vendor type is selected */}
      {filters.locationType.includes('vendor') && (
        <>
          {isLoadingVendors ? (
            <div style={{ display: 'flex', justifyContent: 'center', padding: 16 }}>
              <Spin />
            </div>
          ) : filteredVendors.length > 0 && (
            <>
              <Box className="results-section-header" sx={{
                display: 'flex',
                alignItems: 'center',
                p: 2,
                mb: 1,
                mt: 3,
                backgroundColor: '#fff7e6',
                borderRadius: 1,
                boxShadow: '0 1px 2px rgba(0,0,0,0.05)'
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <VendorIcon sx={{ color: '#fbbc04' }} />
                  <Typography>Vendors ({filteredVendors.length})</Typography>
                </Box>
              </Box>
              <Table
                columns={[
                  {
                    title: 'Company',
                    dataIndex: 'company',
                    key: 'company',
                    width: '250px',
                  },
                  {
                    title: 'Address',
                    dataIndex: 'address',
                    key: 'address',
                    width: '250px',
                  },
                  {
                    title: 'City',
                    dataIndex: 'city',
                    key: 'city',
                    width: '250px',
                  },
                  {
                    title: 'Contact',
                    dataIndex: 'contact',
                    key: 'contact',
                    width: '250px',
                  },
                ]}
                dataSource={filteredVendors.map((vendor: Vendor, index: number) => {
                  const rawContact = [
                    vendor.email_1 && `Email: ${vendor.email_1}`,
                    vendor.phonenumber && `Phone: ${vendor.phonenumber}`,
                  ]
                    .filter(Boolean)
                    .join(' | ');
                  const maskedContact = [
                    vendor.email_1 && `Email: ${maskEmail(vendor.email_1)}`,
                    vendor.phonenumber && `Phone: ${maskPhone(vendor.phonenumber)}`,
                  ]
                    .filter(Boolean)
                    .join(' | ');
                  return {
                    key: `vendor-${index}`,
                    company: vendor.company,
                    address: vendor.address || '-',
                    city: `${vendor.city}, ${vendor.state} ${vendor.zip} ? ${vendor.country?.short_name || ''}`,
                    contact: rawContact ? (
                      <Tooltip title={rawContact}>
                        <span>{maskedContact}</span>
                      </Tooltip>
                    ) : (
                      '-'
                    ),
                    onClick: () => findFeatureAndClick(vendor, vendorSource, 'vendor')
                  };
                })}
                onRow={(record) => ({
                  onClick: record.onClick,
                })}
                pagination={{
                  current: vendorPageSafe,
                  pageSize: VENDOR_PAGE_SIZE,
                  total: filteredVendors.length,
                  onChange: (page) => setVendorPage(page),
                  showSizeChanger: false,
                  showQuickJumper: false,
                  size: 'small'
                }}
                size="small"
                className='border'
                scroll={{x: 'max-content'}}
              />
            </>
          )}
        </>
      )}

      {/* Show prospects when there's a search term */}
      {filters.searchTerm && !filters.productSearch && filteredProspects.length > 0 && (
        <>
          <Box className="results-section-header" sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            p: 2,
            mb: 1,
            mt: 3,
            backgroundColor: '#e8f0fe',
            borderRadius: 1,
            boxShadow: '0 1px 2px rgba(0,0,0,0.05)'
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <ClientIcon sx={{ color: '#1a73e8' }} />
              <Typography>Matching Prospects ({filteredProspects.length})</Typography>
            </Box>
          </Box>
          {paginatedProspects.map((prospect, index) => (
            <ProspectItem 
              key={`prospect-${index}`} 
              prospect={prospect} 
              onClick={() => findFeatureAndClick(prospect, prospectSource, 'prospect')}
            />
          ))}
          {totalProspectPages > 1 && (
            <div style={{ display: 'flex', justifyContent: 'center', marginTop: 12 }}>
              <Pagination current={prospectPageSafe} total={filteredProspects.length} pageSize={PROSPECT_PAGE_SIZE} onChange={(page) => setProspectPage(page)} size="small" />
            </div>
          )}
        </>
      )}

      {filteredLocations.length === 0 &&
        filteredWarehouses.length === 0 &&
        filteredVendors.length === 0 &&
        (!filters.searchTerm || filteredProspects.length === 0) && (
          <Box sx={{ textAlign: 'center', color: '#5f6368', py: 3 }}>
            <LocationIcon sx={{ fontSize: '2rem', mb: 1 }} />
            <Typography>No locations found</Typography>
          </Box>
        )}
    </Box>
  );
};

export default React.memo(SearchResults);
